import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Lock, Send } from 'lucide-react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent } from '../content';
import { postCapsuleMessage, getCapsuleMessageCount } from '@/src/infrastructure/lib/api';
import { SectionBgImage, hasSectionBg } from './SectionBgImage';

interface MemoryCapsuleSectionProps {
  content: BirthdayContent;
  /** Live invitation id — present on published/draft invitations, null in preview. */
  invitationId?: string | null;
  /** True only when the invitation is published/processing (accepts real submissions). */
  isLive?: boolean;
}

export function MemoryCapsuleSection({ content, invitationId, isLive }: MemoryCapsuleSectionProps) {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [liveCount, setLiveCount] = useState<number | null>(null);

  const birthdayPersonName = content.hero.birthdayPersonName;
  const live = Boolean(isLive && invitationId);
  const bg = content.sectionBackgrounds.capsule;
  const active = hasSectionBg(bg);

  // Fetch live count from backend when published so the badge is accurate
  useEffect(() => {
    if (!live || !invitationId) return;
    getCapsuleMessageCount(invitationId)
      .then(({ count }) => setLiveCount(count))
      .catch(() => {});
  }, [live, invitationId, sent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !message.trim() || sending) return;
    setError(null);

    // Preview / draft: confirm locally without a network call.
    if (!live || !invitationId) {
      setSent(true);
      setName('');
      setMessage('');
      setTimeout(() => setSent(false), 4000);
      return;
    }

    // Published: persist the private message through the API.
    setSending(true);
    try {
      await postCapsuleMessage(invitationId, { senderName: name.trim(), message: message.trim() });
      setSent(true);
      setName('');
      setMessage('');
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not seal your message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    'w-full rounded-xl border border-purple-400/25 bg-white/5 px-4 py-3 text-white placeholder:text-purple-400/40 outline-none focus:border-purple-400/60 focus:ring-2 focus:ring-purple-400/10 transition-all text-sm';

  return (
    <section
      className="relative py-24 px-6 overflow-hidden"
      style={active ? undefined : { background: 'linear-gradient(180deg, #0f172a 0%, #1a103f 100%)' }}
    >
      <SectionBgImage bg={bg} />
      {/* soft purple glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-purple-600/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-xl mx-auto text-center">
        {/* lock icon */}
        <motion.div
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ type: 'spring', stiffness: 250, damping: 18 }}
          className="inline-flex w-16 h-16 rounded-full bg-purple-500/20 border border-purple-400/30 items-center justify-center mb-6 mx-auto"
        >
          <Lock size={28} className="text-purple-300" />
        </motion.div>

        {/* heading */}
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: BDAY_EASE }}
          className="text-purple-400 text-xs font-bold uppercase tracking-[0.25em] mb-2"
        >
          🔮 Future Messages
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1, ease: BDAY_EASE }}
          className="text-3xl sm:text-4xl font-bold text-white mb-3"
        >
          Memory Capsule
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-purple-200 text-sm leading-relaxed mb-10"
        >
          Write a message for <strong>{birthdayPersonName}</strong> to read in the future.
          Your words will be kept safe until they're ready to open them. ✨
        </motion.p>

        {/* form */}
        <AnimatePresence mode="wait">
          {sent ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="bg-purple-500/15 border border-purple-400/30 rounded-3xl p-8 text-center"
            >
              <p className="text-4xl mb-3">🔒</p>
              <p className="text-white font-bold text-lg">Message Sealed!</p>
              <p className="text-purple-200 text-sm mt-2">Your memory has been added to the capsule.</p>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: BDAY_EASE }}
              onSubmit={handleSubmit}
              className="bg-white/5 backdrop-blur-sm border border-purple-400/20 rounded-3xl p-6 flex flex-col gap-4 text-left"
            >
              <input
                className={inputClass}
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={60}
                required
              />
              <textarea
                className={`${inputClass} resize-none`}
                placeholder={`Write something meaningful for ${birthdayPersonName} to read someday...`}
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={500}
                required
              />
              <button
                type="submit"
                disabled={sending}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-700 text-white font-bold text-sm rounded-2xl hover:from-purple-400 hover:to-purple-600 transition-all shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Lock size={14} />
                {sending ? 'Sealing…' : 'Seal My Message'}
                <Send size={14} />
              </button>
              {error && <p className="text-red-300 text-xs font-semibold">{error}</p>}
            </motion.form>
          )}
        </AnimatePresence>

        {(() => {
          const count = liveCount ?? content.memoryCapsuleMessages.length;
          return count > 0 ? (
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-6 text-purple-400 text-xs"
            >
              💌 {count} message{count > 1 ? 's' : ''} sealed in the capsule
            </motion.p>
          ) : null;
        })()}
      </div>
    </section>
  );
}
